import type { ReactNode } from "react";
import { Label } from "./Label";
import { cn } from "@ridereserv/shared/utils";

export interface FormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Form field wrapper with label, error, and hint support.
 * Uses semantic color tokens from the design system.
 * @param root0 - FormField props
 * @param root0.label - Field label text
 * @param root0.htmlFor - ID of the associated input
 * @param root0.required - Whether the field is required
 * @param root0.error - Error message to display
 * @param root0.hint - Hint text to display
 * @param root0.children - Input element(s)
 * @param root0.className - Additional CSS classes
 * @returns A form field component with label and error handling
 */
export function FormField({
  label,
  htmlFor,
  required,
  error,
  hint,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={htmlFor} required={required}>
        {label}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-neutral-500">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
